import { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useAssistantContext } from '@triedge/ai-assistant-ui';
import type { AssistantContextSummary } from '@triedge/ai-assistant-ui';
import type { AdmissionsAIContext } from './types';
import type { AIAssistantModuleConfig } from './aiModuleConfig';
import { useLazyDomainRouterQuery } from '../../state/domainRouterHooks';
import { normalizeAssistantPersona } from '../../config/ai-assistant/capability-matrix';

type FiltersChangeEvent = CustomEvent<{ filters: AdmissionsAIContext }>;

type ApplicationSummary = {
  id?: string;
  code?: string;
  children?: Array<{
    id?: string;
    childInfo?: {
      firstName?: string;
      lastName?: string;
    };
  }>;
};

type UseAdmissionsAssistantContextOptions = {
  initialContext?: AdmissionsAIContext;
  isActive: boolean;
  moduleConfig: AIAssistantModuleConfig;
  pathname?: string;
};

type UseAdmissionsAssistantContextResult = {
  context: AdmissionsAIContext;
  setContext: Dispatch<SetStateAction<AdmissionsAIContext>>;
  summary: AssistantContextSummary;
  resultCount: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  readStoredContext: () => Partial<AdmissionsAIContext>;
};

const parseStoredFilters = (value: string | null): Partial<AdmissionsAIContext> | undefined => {
  if (!value) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as Partial<AdmissionsAIContext>;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const normalizeStoredPersonaRole = (
  value: string | null
): AdmissionsAIContext['personaRole'] | undefined => {
  const normalized = normalizeAssistantPersona(value);
  if (
    normalized === 'staff'
    || normalized === 'nurse'
    || normalized === 'parent'
    || normalized === 'admin'
    || normalized === 'owner'
    || normalized === 'editor'
    || normalized === 'viewer'
    || normalized === 'super-administrator'
  ) {
    return normalized;
  }
  return undefined;
};

const parseRouteApplicationId = (value: string): string | null => {
  const pathname = String(value ?? '').trim();
  if (!pathname) {
    return null;
  }
  const patterns = [
    /\/school\/financial(?:aid|-aid)\/staff\/([^/?#]+)/i,
    /\/school\/admissions\/(?:children|family|documents|notes)\/([^/?#]+)/i,
  ];
  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (!match?.[1]) {
      continue;
    }
    try {
      return decodeURIComponent(match[1]).trim() || null;
    } catch {
      return String(match[1]).trim() || null;
    }
  }
  return null;
};

const toApplicationSubjectLabel = (
  applicationCode: string | null | undefined,
  applicationId: string
): string => {
  const normalizedCode = String(applicationCode ?? '').trim();
  if (normalizedCode.length > 0) {
    return `Application ${normalizedCode}`;
  }
  return `Application ${applicationId}`;
};

const readLocalStorageContext = (pathnameOverride?: string): Partial<AdmissionsAIContext> => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const pathname = String(pathnameOverride ?? window.location.pathname ?? '').trim();
    const routeApplicationId = parseRouteApplicationId(pathname);
    const storedFilters = window.localStorage.getItem('admissions_filters');
    const storedPersona = window.localStorage.getItem('triedge_persona');
    const activeApplicationId = window.localStorage.getItem('admissions_ai_active_application_id');
    const activeApplicationCode = window.localStorage.getItem('admissions_ai_active_application_code');
    const activeStudentId = window.localStorage.getItem('admissions_ai_active_student_id');
    const activeStudentName = window.localStorage.getItem('admissions_ai_active_student_name');
    const onBehalfSubjectId = window.localStorage.getItem('admissions_ai_on_behalf_subject_id');
    const onBehalfSubjectType = window.localStorage.getItem('admissions_ai_on_behalf_subject_type');
    const onBehalfSubjectLabel = window.localStorage.getItem('admissions_ai_on_behalf_subject_label');
    const onBehalfRouteHint = window.localStorage.getItem('admissions_ai_on_behalf_route_hint');
    const parsedFilters = parseStoredFilters(storedFilters);
    const normalizedStoredPersona = normalizeStoredPersonaRole(storedPersona);

    return {
      ...(parsedFilters ?? {}),
      ...(normalizedStoredPersona ? { personaRole: normalizedStoredPersona } : {}),
      ...(activeApplicationId ? { activeApplicationId } : {}),
      ...(activeApplicationCode ? { activeApplicationCode } : {}),
      ...(activeStudentId ? { activeStudentId } : {}),
      ...(activeStudentName ? { activeStudentName } : {}),
      ...(onBehalfSubjectId && onBehalfSubjectType
        ? {
          onBehalfOf: {
            subjectId: onBehalfSubjectId,
            subjectType: onBehalfSubjectType,
            ...(onBehalfSubjectLabel ? { subjectLabel: onBehalfSubjectLabel } : {}),
            ...(onBehalfRouteHint ? { routeHint: onBehalfRouteHint } : {}),
          },
        }
        : {}),
      ...(routeApplicationId
        ? {
          activeApplicationId: routeApplicationId,
          selectedIds: [routeApplicationId],
          onBehalfOf: {
            subjectId: routeApplicationId,
            subjectType: 'application',
            subjectLabel: toApplicationSubjectLabel(activeApplicationCode, routeApplicationId),
            routeHint: pathname,
          },
        }
        : {}),
    };
  } catch (error) {
    console.warn('[AdmissionsAI] Failed to read saved context', error);
    return {};
  }
};

const writeLocalStorageContext = (next: Partial<AdmissionsAIContext>) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (next.activeApplicationId) {
    window.localStorage.setItem('admissions_ai_active_application_id', next.activeApplicationId);
  }
  if (next.activeApplicationCode) {
    window.localStorage.setItem('admissions_ai_active_application_code', next.activeApplicationCode);
  }
  if (next.activeStudentId) {
    window.localStorage.setItem('admissions_ai_active_student_id', next.activeStudentId);
  }
  if (next.activeStudentName) {
    window.localStorage.setItem('admissions_ai_active_student_name', next.activeStudentName);
  }
  if (next.onBehalfOf?.subjectId && next.onBehalfOf?.subjectType) {
    window.localStorage.setItem('admissions_ai_on_behalf_subject_id', next.onBehalfOf.subjectId);
    window.localStorage.setItem('admissions_ai_on_behalf_subject_type', next.onBehalfOf.subjectType);
    if (next.onBehalfOf.subjectLabel) {
      window.localStorage.setItem('admissions_ai_on_behalf_subject_label', next.onBehalfOf.subjectLabel);
    }
    if (next.onBehalfOf.routeHint) {
      window.localStorage.setItem('admissions_ai_on_behalf_route_hint', next.onBehalfOf.routeHint);
    }
  }
};

const mergeContextIfChanged = (
  previous: AdmissionsAIContext,
  updates: Partial<AdmissionsAIContext>,
): AdmissionsAIContext => {
  const next = { ...previous, ...updates };
  const changed = Object.keys(updates).some((key) => {
    const typedKey = key as keyof AdmissionsAIContext;
    const prevValue = previous[typedKey];
    const nextValue = next[typedKey];
    if (Array.isArray(prevValue) || Array.isArray(nextValue)) {
      const prevArray = Array.isArray(prevValue) ? prevValue : [];
      const nextArray = Array.isArray(nextValue) ? nextValue : [];
      if (prevArray.length !== nextArray.length) return true;
      return prevArray.some((entry, index) => entry !== nextArray[index]);
    }
    return prevValue !== nextValue;
  });
  return changed ? next : previous;
};

const resolveContextError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (message.includes('Throughput exceeds')) {
    console.warn('[AdmissionsAI] Context query throttled');
    return 'Context temporarily unavailable. Retry in a moment.';
  }
  console.error('[AdmissionsAI] Failed to load context:', error);
  return 'Unable to load context details.';
};

const buildApplicationsQueryArgs = (context: AdmissionsAIContext) => {
  const filter: Record<string, unknown> = {};
  if (context.status?.length) {
    filter.status = context.status;
  }
  if (context.grade?.length) {
    filter.gradeLevel = context.grade;
  }
  if (context.assignedTo) {
    filter.assignedTo = context.assignedTo;
  }
  if (context.search?.trim()) {
    filter.search = context.search.trim();
  }
  if (Object.keys(filter).length === 0) {
    return {};
  }
  return { filter };
};

const parseApplicationsResult = (payload: unknown): ApplicationSummary[] => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const root = (payload as { listApplications?: unknown }).listApplications ?? payload;
  if (!root || typeof root !== 'object') {
    return [];
  }
  const items = (root as { items?: unknown }).items;
  return Array.isArray(items) ? (items as ApplicationSummary[]) : [];
};

export function useAdmissionsAssistantContext({
  initialContext,
  isActive,
  moduleConfig,
  pathname,
}: UseAdmissionsAssistantContextOptions): UseAdmissionsAssistantContextResult {
  const [triggerListApplications] = useLazyDomainRouterQuery<'listApplications'>();
  const storage = useMemo(() => ({
    read: () => readLocalStorageContext(pathname),
    write: writeLocalStorageContext,
  }), [pathname]);
  const fetchContext = useCallback(async (activeContext: AdmissionsAIContext) => {
    const applicationsPayload = await triggerListApplications(
      'listApplications',
      buildApplicationsQueryArgs(activeContext)
    );
    const applications = parseApplicationsResult(applicationsPayload);
    const resultCount = applications.length;
    const summaryResult: AssistantContextSummary = {
      count: resultCount,
      label: moduleConfig.entityLabel,
      searchLabel: activeContext.search,
    };

    if (!moduleConfig.supportsApplicationContext) {
      return { summary: summaryResult };
    }

    if (activeContext.personaRole === 'parent' && applications.length > 0) {
      const [firstApp] = applications;
      const firstChild = firstApp?.children?.[0];
      const nextContext: Partial<AdmissionsAIContext> = {};

      if (!activeContext.activeApplicationId && firstApp?.id) {
        nextContext.activeApplicationId = firstApp.id;
      }
      if (!activeContext.activeApplicationCode && firstApp?.code) {
        nextContext.activeApplicationCode = firstApp.code;
      }
      if (!activeContext.activeStudentId && firstChild?.id) {
        nextContext.activeStudentId = firstChild.id;
      }
      if (!activeContext.activeStudentName && firstChild?.childInfo) {
        const firstName = firstChild.childInfo.firstName ?? '';
        const lastName = firstChild.childInfo.lastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
          nextContext.activeStudentName = fullName;
        }
      }

      return { summary: summaryResult, nextContext };
    }

    return { summary: summaryResult };
  }, [moduleConfig.entityLabel, moduleConfig.supportsApplicationContext, triggerListApplications]);
  const {
    context,
    setContext,
    summary,
    isLoading,
    error,
    refresh,
    readStoredContext,
  } = useAssistantContext<AdmissionsAIContext>({
    initialContext,
    isActive,
    storage,
    fetchContext: moduleConfig.supportsContextFetch ? fetchContext : undefined,
    onError: resolveContextError,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleFilterChange = (event: FiltersChangeEvent) => {
      setContext((prev) => mergeContextIfChanged(prev, event.detail.filters));
    };

    window.addEventListener(
      'triedge:filters-change' as unknown as string,
      handleFilterChange as EventListener
    );
    return () => window.removeEventListener(
      'triedge:filters-change' as unknown as string,
      handleFilterChange as EventListener
    );
  }, [setContext]);

  useEffect(() => {
    const routeApplicationId = parseRouteApplicationId(String(pathname ?? ''));
    if (!routeApplicationId) {
      return;
    }
    setContext((previous) => mergeContextIfChanged(previous, {
      activeApplicationId: routeApplicationId,
      selectedIds: [routeApplicationId],
      onBehalfOf: {
        subjectId: routeApplicationId,
        subjectType: 'application',
        subjectLabel: toApplicationSubjectLabel(previous.activeApplicationCode, routeApplicationId),
        routeHint: String(pathname ?? ''),
      },
    }));
  }, [pathname, setContext]);

  return {
    context,
    setContext,
    summary,
    resultCount: summary.count ?? 0,
    isLoading,
    error,
    refresh,
    readStoredContext,
  };
}
